import { getStoredActiveFacilityId } from '../../services/api';
import { cleanSlotRange } from '../../utils/slotName';

export const toCamelCase = (str) => str.replace(/_([a-z])/g, (g) => g[1].toUpperCase());

export const toSnakeCase = (str) => str.replace(/[A-Z]/g, (letter) => `_${letter.toLowerCase()}`);

export const transformBookingToAPI = (booking) => {
  if (!booking) return booking;

  const teamDetails = booking.teamDetails;
  const teamA = teamDetails?.teamA;
  const teamB = teamDetails?.teamB;

  let guests = booking.guestList;
  if (Array.isArray(guests)) {
    guests = guests.map((guest) => (typeof guest === 'string' ? guest : guest.name || guest));
  }

  return {
    ground_id: booking.groundId,
    service_id: booking.serviceId,
    slot_id: booking.slotId,
    date: booking.date,
    time_slot: booking.timeSlot,
    slot_name: booking.slotName,
    booking_type: booking.bookingType,
    service_category: booking.serviceCategory || (booking.serviceType === 'cricket_match' ? 'team_sports' : 'individual_sports'),
    service_type: booking.serviceType,
    team_a_name: teamA?.name || booking.teamAName,
    team_a_captain_name: teamA?.captain || booking.teamACaptainName,
    team_a_captain_phone: teamA?.phone || booking.teamACaptainPhone,
    team_a_players: teamA?.players || booking.teamAPlayers || [],
    team_b_name: teamB?.name || booking.teamBName,
    team_b_captain_name: teamB?.captain || booking.teamBCaptainName,
    team_b_captain_phone: teamB?.phone || booking.teamBCaptainPhone,
    team_b_players: teamB?.players || booking.teamBPlayers || [],
    contact_name: booking.contactName || booking.bookedBy,
    contact_phone: booking.contactPhone || booking.bookedByPhone,
    contact_email: booking.contactEmail,
    duration: booking.duration,
    player_count: booking.playerCount || (guests ? guests.length : 0),
    guests: guests || [],
    base_amount:
      booking.baseAmount !== undefined && booking.baseAmount !== null
        ? booking.baseAmount
        : booking.totalAmount !== undefined && booking.totalAmount !== null
          ? booking.totalAmount
          : 0,
    discount_amount: booking.discountAmount !== undefined && booking.discountAmount !== null ? booking.discountAmount : 0,
    discount_type: booking.discountType || booking.discount_type || 'amount',
    total_amount: booking.totalAmount !== undefined && booking.totalAmount !== null ? booking.totalAmount : 0,
    advance_amount: booking.advanceAmount !== undefined && booking.advanceAmount !== null ? booking.advanceAmount : 0,
    balance_amount:
      booking.balanceAmount !== undefined && booking.balanceAmount !== null
        ? booking.balanceAmount
        : booking.totalAmount !== undefined && booking.totalAmount !== null
          ? booking.totalAmount - (booking.advanceAmount !== undefined && booking.advanceAmount !== null ? booking.advanceAmount : 0)
          : 0,
    payment_mode: booking.paymentMode || booking.payment_mode || 'cash',
    booked_team_side: booking.bookedTeamSide || null,
    booked_by: booking.bookedBy || booking.contactName,
    booked_by_phone: booking.bookedByPhone || booking.contactPhone,
  };
};

export const pickFacilityRecord = (data, activeId) => {
  const list = Array.isArray(data) ? data : data ? [data] : [];
  if (!list.length) return null;
  if (activeId == null) return list[0];
  const match = list.find((f) => Number(f.id) === Number(activeId));
  return match || list[0];
};

export const transformBookingFromAPI = (booking) => {
  if (!booking) return booking;

  if (booking.totalAmount !== undefined && booking.bookingDate !== undefined) {
    return booking;
  }

  const transformed = {};
  for (const key in booking) {
    transformed[toCamelCase(key)] = booking[key];
  }

  let timeSlot = transformed.timeSlot || transformed.time_slot || '';
  if (!timeSlot && transformed.slotStartTime && transformed.slotEndTime) {
    const formatTime = (timeStr) => {
      if (!timeStr) return '';
      if (timeStr.includes(':')) {
        const parts = timeStr.split(':');
        return `${parts[0]}:${parts[1]}`;
      }
      return timeStr;
    };
    const startTime = formatTime(transformed.slotStartTime);
    const endTime = formatTime(transformed.slotEndTime);
    if (startTime && endTime) {
      timeSlot = `${startTime}-${endTime}`;
    }
  }

  // GA-004: normalise to a canonical raw range so consumers never render the
  // doubled/tripled "06:00–10:00 (06:00-10:00)" form. The human-readable slot
  // name lives separately in `slotName`; display helpers compose the two.
  timeSlot = cleanSlotRange(timeSlot);

  const toNumber = (val, defaultValue = 0) => {
    if (val === null || val === undefined) return defaultValue;
    const num = typeof val === 'string' ? parseFloat(val) : Number(val);
    return Number.isNaN(num) ? defaultValue : num;
  };

  const firstDefined = (...values) => values.find((v) => v !== null && v !== undefined);

  const rawTotalAmount = firstDefined(transformed.totalAmount, transformed.total_amount);
  const rawAdvanceAmount = firstDefined(transformed.advanceAmount, transformed.advance_amount);
  const rawBalanceAmount = firstDefined(transformed.balanceAmount, transformed.balance_amount);
  const rawBaseAmount = firstDefined(transformed.baseAmount, transformed.base_amount);
  const rawDiscountAmount = firstDefined(transformed.discountAmount, transformed.discount_amount);

  const inferBookedTeamSide = (row) => {
    const explicit = row.bookedTeamSide || row.booked_team_side;
    if (explicit === 'teamA' || explicit === 'teamB') return explicit;
    if ((row.bookingType || row.booking_type) === 'both') return null;
    const hasA = String(row.teamAName || row.team_a_name || '').trim().length > 0;
    const hasB = String(row.teamBName || row.team_b_name || '').trim().length > 0;
    if (hasA && !hasB) return 'teamA';
    if (hasB && !hasA) return 'teamB';
    return null;
  };

  const bookedTeamSide = inferBookedTeamSide({ ...transformed, ...booking });
  const bookingType = transformed.bookingType || transformed.booking_type || booking.booking_type || null;
  const linkedBookingIds = transformed.linkedBookingIds || transformed.linked_booking_ids || null;

  return {
    ...transformed,
    bookingType,
    bookedTeamSide,
    bookingIds: Array.isArray(linkedBookingIds) ? linkedBookingIds : undefined,
    matchId: transformed.matchId || transformed.match_id || undefined,
    totalAmount: toNumber(rawTotalAmount, 0),
    advanceAmount: toNumber(rawAdvanceAmount, 0),
    balanceAmount: toNumber(rawBalanceAmount, toNumber(rawTotalAmount, 0) - toNumber(rawAdvanceAmount, 0)),
    baseAmount: toNumber(rawBaseAmount, 0),
    discountAmount: toNumber(rawDiscountAmount, 0),
    bookingDate:
      transformed.bookingDate || transformed.booking_date || transformed.createdAt || transformed.created_at || new Date().toISOString(),
    serviceName: transformed.serviceName || transformed.service_name || '',
    groundName: transformed.groundName || transformed.ground_name || '',
    serviceType: transformed.serviceType || transformed.service_type || '',
    serviceCategory: transformed.serviceCategory || transformed.service_category || '',
    timeSlot,
    slotName: transformed.slotName || transformed.slot_name || '',
    contactName: transformed.contactName || transformed.contact_name || transformed.bookedBy || transformed.booked_by || '',
    contactPhone: transformed.contactPhone || transformed.contact_phone || transformed.bookedByPhone || transformed.booked_by_phone || '',
    contactEmail: transformed.contactEmail || transformed.contact_email || '',
    paymentStatus:
      transformed.paymentStatus ||
      transformed.payment_status ||
      (toNumber(rawAdvanceAmount, 0) >= toNumber(rawTotalAmount, 0) ? 'fully_paid' : 'pending'),
    paymentMode: transformed.paymentMode || transformed.payment_mode || 'cash',
    playerCount: toNumber(firstDefined(transformed.playerCount, transformed.player_count), 0),
    teamDetails:
      transformed.teamDetails ||
      (transformed.teamAName || transformed.team_a_name || transformed.teamBName || transformed.team_b_name
        ? {
            teamA: {
              name: transformed.teamAName || transformed.team_a_name || '',
              captain: transformed.teamACaptainName || transformed.team_a_captain_name || '',
              phone: transformed.teamACaptainPhone || transformed.team_a_captain_phone || '',
              players: transformed.teamAPlayers || transformed.team_a_players || [],
            },
            teamB: {
              name: transformed.teamBName || transformed.team_b_name || '',
              captain: transformed.teamBCaptainName || transformed.team_b_captain_name || '',
              phone: transformed.teamBCaptainPhone || transformed.team_b_captain_phone || '',
              players: transformed.teamBPlayers || transformed.team_b_players || [],
            },
          }
        : null),
    guestList:
      transformed.guestList ||
      (transformed.guests
        ? Array.isArray(transformed.guests)
          ? transformed.guests.map((guest) => (typeof guest === 'string' ? { name: guest } : guest))
          : []
        : []),
    venue: transformed.venue || transformed.groundName || transformed.ground_name || '',
    location: transformed.location || transformed.groundName || transformed.ground_name || '',
    date: transformed.date || transformed.bookingDate || transformed.booking_date || '',
    id: toNumber(firstDefined(transformed.id, booking.id)),
    groundId: toNumber(firstDefined(transformed.groundId, transformed.ground_id, booking.ground_id)),
    serviceId: toNumber(firstDefined(transformed.serviceId, transformed.service_id, booking.service_id)),
    facilityId: (() => {
      const raw = firstDefined(transformed.facilityId, transformed.facility_id, booking.facility_id);
      if (raw == null || raw === '') return undefined;
      const n = toNumber(raw, NaN);
      return Number.isNaN(n) ? undefined : n;
    })(),
    slotId: transformed.slotId || transformed.slot_id || booking.slot_id,
    status: transformed.status || booking.status || 'confirmed',
    price: toNumber(firstDefined(rawTotalAmount, transformed.price), 0),
  };
};

export const transformSlotConfigFromAPI = (apiConfigs) => {
  if (!apiConfigs || !Array.isArray(apiConfigs) || apiConfigs.length === 0) {
    return { dateRange: null, services: [] };
  }

  const firstConfig = apiConfigs[0];
  const dateRange =
    firstConfig.date_range_start && firstConfig.date_range_end
      ? {
          startDate: new Date(firstConfig.date_range_start),
          endDate: new Date(firstConfig.date_range_end),
        }
      : null;

  const services = apiConfigs.map((config) => {
    const service = {
      id: config.service_id,
      ground_id: config.service_ground_id ?? config.ground_id,
      name: config.service_name || 'Service',
      type: config.service_type || 'cricket_match',
      category: config.service_category || 'team_sports',
      slotsPerDay: config.slots_per_day || 0,
      slotDuration: config.slot_duration || null,
      firstSlotStartTime: config.first_slot_start_time || null,
      breakBetweenSlots: config.break_between_slots || 0,
    };

    const slots = (config.slots || []).map((slot) => ({
      id: slot.id,
      name: slot.name || null,
      startTime: slot.start_time ? slot.start_time.substring(0, 5) : null,
      endTime: slot.end_time ? slot.end_time.substring(0, 5) : null,
      defaultPrice: parseFloat(slot.default_price) || 0,
      dayOfWeek: slot.day_of_week !== null && slot.day_of_week !== undefined ? slot.day_of_week : null,
    }));

    const weeklyPrices = config.weeklyPrices || {};
    const formattedWeeklyPrices = {
      sunday: {},
      monday: {},
      tuesday: {},
      wednesday: {},
      thursday: {},
      friday: {},
      saturday: {},
    };

    const dayNames = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
    dayNames.forEach((dayName) => {
      const dayPrices = weeklyPrices[dayName] || {};
      Object.keys(dayPrices).forEach((configuredSlotId) => {
        const matchingSlot = slots.find((slot) => slot.id === parseInt(configuredSlotId, 10));
        if (matchingSlot) {
          formattedWeeklyPrices[dayName][matchingSlot.id] = parseFloat(dayPrices[configuredSlotId]) || 0;
        } else {
          formattedWeeklyPrices[dayName][configuredSlotId] = parseFloat(dayPrices[configuredSlotId]) || 0;
        }
      });
    });

    return {
      slotConfigurationId: config.id,
      service: { ...service, slots },
      slots,
      priceSetup: { weeklyPrices: formattedWeeklyPrices },
      // Date-specific holiday overrides, keyed 'YYYY-MM-DD'.
      holidayPrices: config.holidayPrices || {},
    };
  });

  return { dateRange, services };
};

export const transformIndividualSportsPricesFromAPI = (apiPrices, facilityServices = []) => {
  if (!Array.isArray(apiPrices) || apiPrices.length === 0) {
    return [];
  }

  const groupedByService = apiPrices.reduce((acc, row) => {
    const serviceId = Number(row.service_id);
    if (!serviceId) return acc;

    if (!acc[serviceId]) {
      acc[serviceId] = {
        serviceId,
        serviceName: row.service_name || '',
        prices: {},
      };
    }

    const numericPrice = parseFloat(row.price);
    if (!Number.isNaN(numericPrice) && numericPrice > 0) {
      acc[serviceId].prices[row.duration] = numericPrice;
    }

    return acc;
  }, {});

  return Object.values(groupedByService).map((servicePrices) => {
    const matchedService = facilityServices.find((svc) => Number(svc.id) === servicePrices.serviceId);

    return {
      service: {
        id: servicePrices.serviceId,
        name: matchedService?.name || servicePrices.serviceName || `Service ${servicePrices.serviceId}`,
        type: matchedService?.type || 'individual_sports',
        category: matchedService?.category || 'individual_sports',
        description: matchedService?.description || '',
      },
      priceSetup: { prices: servicePrices.prices },
    };
  });
};

export const loadFromStorage = (key, defaultValue) => {
  try {
    if (typeof window === 'undefined' || !window.localStorage) {
      return defaultValue;
    }
    const item = localStorage.getItem(key);
    if (item) {
      const parsed = JSON.parse(item);
      if (parsed && parsed.dateRange) {
        if (parsed.dateRange.startDate) {
          parsed.dateRange.startDate = new Date(parsed.dateRange.startDate);
        }
        if (parsed.dateRange.endDate) {
          parsed.dateRange.endDate = new Date(parsed.dateRange.endDate);
        }
      }
      return parsed;
    }
  } catch (error) {
    console.error(`Error loading ${key} from localStorage:`, error);
    return defaultValue;
  }
  return defaultValue;
};

export const scopedKey = (baseKey, facilityId) => {
  if (facilityId == null || facilityId === '') return `${baseKey}:__none__`;
  return `${baseKey}:${facilityId}`;
};

export const resolveFacilityScopeId = (activeId) => {
  if (activeId != null && activeId !== '') {
    const n = Number(activeId);
    return Number.isNaN(n) ? getStoredActiveFacilityId() : n;
  }
  return getStoredActiveFacilityId();
};

export const useAPIMode = () => process.env.REACT_APP_USE_LOCAL_STORAGE !== 'true';

// ─── Slot price resolution helpers ───────────────────────────────────────────
//
// These utilities centralise the multi-fallback price look-up that was
// previously duplicated across MyBookings, BookingsOverview, and
// useBookingPage.  All three files now import from here.

/**
 * Coerce any value to a finite number, or null if it cannot be parsed.
 *
 * @param {*} value
 * @returns {number|null}
 */
export const toAmount = (value) => {
  if (value === null || value === undefined || value === '') return null;
  const num = typeof value === 'number' ? value : parseFloat(value);
  return Number.isFinite(num) ? num : null;
};

/** HH:MM normalisation for time strings. */
const normalizeTime = (timeValue) => {
  if (!timeValue || typeof timeValue !== 'string') return '';
  return timeValue.slice(0, 5);
};

/**
 * Resolve the configured price for a given slot + date from the facility's
 * slot-configuration data structure.
 *
 * Resolution order:
 *  1. Day-specific price keyed by slot ID (numeric or string)
 *  2. Day-specific price keyed by the time-range-matched configured slot ID
 *  3. Slot's `defaultPrice` / `price` field
 *  4. First non-zero price for this slot across any day (cross-day fallback)
 *
 * @param {Object} params
 * @param {Object}   params.serviceConfig  One entry from slotConfiguration.services
 * @param {number|string} params.slotId    The selected slot's ID
 * @param {string|Date}   params.date      The booking date
 * @param {Object[]}  [params.extraSlots]  Additional slot lists to search for time-range matching
 * @returns {number|null} Resolved price, or null if not found
 */
export const resolveSlotPrice = ({ serviceConfig, slotId, date, extraSlots = [] }) => {
  if (!serviceConfig?.priceSetup?.weeklyPrices || slotId == null || !date) return null;

  const weeklyPrices = serviceConfig.priceSetup.weeklyPrices;
  const configuredSlots = serviceConfig?.service?.slots || serviceConfig?.slots || [];
  const allKnownSlots = [...configuredSlots, ...extraSlots];

  let dayName = null;
  let dateKey = null;
  try {
    const d = date instanceof Date ? date : new Date(date);
    if (!Number.isNaN(d.getTime())) {
      // Sunday=0, Monday=1, … Saturday=6
      dayName = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'][d.getDay()];
      const pad = (n) => String(n).padStart(2, '0');
      dateKey = `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
    }
  } catch { /* ignore */ }

  // 0. Holiday (date-specific) override beats every recurring price.
  const holiday = dateKey && serviceConfig.holidayPrices
    ? serviceConfig.holidayPrices[dateKey]
    : null;
  if (holiday && holiday.price != null) {
    if (holiday.priceUnit === 'per_hour') {
      const slot = allKnownSlots.find((s) => String(s.id) === String(slotId));
      const start = slot ? normalizeTime(slot.startTime) : null;
      const end = slot ? normalizeTime(slot.endTime) : null;
      if (start && end) {
        const toMin = (t) => { const [h, m] = t.split(':').map(Number); return h * 60 + m; };
        const hours = (toMin(end) - toMin(start)) / 60;
        if (hours > 0) return Math.round(holiday.price * hours * 100) / 100;
      }
    }
    return toAmount(holiday.price);
  }

  const dayPrices = (dayName && weeklyPrices[dayName]) ? weeklyPrices[dayName] : {};
  const slotIdNum = slotId;
  const slotIdStr = String(slotId);

  // 1. Direct key match in day prices
  const direct = toAmount(dayPrices[slotIdNum] ?? dayPrices[slotIdStr]);
  if (direct !== null) return direct;

  // 2. Time-range mapping: find the configured slot whose time range matches
  //    the selected slot, then look up its ID in day prices.
  const selectedCfgSlot = allKnownSlots.find((s) => String(s.id) === slotIdStr);
  const selStart = selectedCfgSlot ? normalizeTime(selectedCfgSlot.startTime) : null;
  const selEnd = selectedCfgSlot ? normalizeTime(selectedCfgSlot.endTime) : null;

  if (selStart && selEnd) {
    const mappedSlot = configuredSlots.find(
      (s) => normalizeTime(s.startTime) === selStart && normalizeTime(s.endTime) === selEnd,
    );
    if (mappedSlot) {
      const viaMap = toAmount(dayPrices[mappedSlot.id] ?? dayPrices[String(mappedSlot.id)]);
      if (viaMap !== null) return viaMap;
    }
  }

  // 3. Slot's own defaultPrice
  const defSlot = allKnownSlots.find((s) => String(s.id) === slotIdStr);
  const defPrice = toAmount(defSlot?.defaultPrice ?? defSlot?.price);
  if (defPrice !== null) return defPrice;

  // 4. Cross-day fallback: first non-zero price for this slot ID across all days
  for (const prices of Object.values(weeklyPrices)) {
    if (!prices || typeof prices !== 'object') continue;
    const crossDay = toAmount(prices[slotIdNum] ?? prices[slotIdStr]);
    if (crossDay !== null && crossDay > 0) return crossDay;

    // Time-range cross-day
    if (selStart && selEnd) {
      const mappedSlot = configuredSlots.find(
        (s) => normalizeTime(s.startTime) === selStart && normalizeTime(s.endTime) === selEnd,
      );
      if (mappedSlot) {
        const viaMap = toAmount(prices[mappedSlot.id] ?? prices[String(mappedSlot.id)]);
        if (viaMap !== null && viaMap > 0) return viaMap;
      }
    }
  }

  return null;
};

/**
 * Given a booking record and the facility's slot configuration, resolve the
 * most reliable total amount for that booking.
 *
 * Resolution order:
 *  1. booking.totalAmount (if > 0)
 *  2. booking.baseAmount − booking.discountAmount
 *  3. Configured slot price for this booking's slot + date
 *  4. booking.price (last resort)
 *
 * @param {Object} booking
 * @param {Object|null} slotConfiguration  The full slotConfiguration object
 *   (shape: { services: Array<{ service, slots, priceSetup }> })
 * @returns {number}
 */
export const resolveBookingTotalAmount = (booking, slotConfiguration) => {
  const directTotal = toAmount(booking.totalAmount) ?? toAmount(booking.paymentDetails?.totalAmount);
  if (directTotal !== null && directTotal > 0) return directTotal;

  const baseAmount = toAmount(booking.baseAmount) ?? toAmount(booking.paymentDetails?.baseAmount);
  const discountAmount = toAmount(booking.discountAmount) ?? toAmount(booking.paymentDetails?.discountAmount) ?? 0;
  const computedFromBase = baseAmount !== null ? Math.max(0, baseAmount - discountAmount) : null;
  if (computedFromBase !== null && computedFromBase > 0) return computedFromBase;

  // Try slot configuration price
  const serviceConfigs = slotConfiguration?.services || [];
  const matchedConfig = serviceConfigs.find((config) => {
    const svc = config?.service || {};
    const cfgId = svc.id != null ? Number(svc.id) : null;
    const bkgId = booking.serviceId != null ? Number(booking.serviceId) : null;
    if (cfgId !== null && bkgId !== null) return cfgId === bkgId;
    return (svc.name || '') === (booking.serviceName || '');
  });

  if (matchedConfig) {
    // Determine slotId: try direct match, then time-range fallback
    const configuredSlots = matchedConfig.slots || [];
    const matchedSlot = configuredSlots.find((slot) => {
      const slotIdStr = slot.id != null ? String(slot.id) : '';
      const bkgSlotIdStr = booking.slotId != null ? String(booking.slotId) : '';
      if (slotIdStr && bkgSlotIdStr && slotIdStr === bkgSlotIdStr) return true;
      const slotRange = `${slot.startTime || ''}-${slot.endTime || ''}`.toLowerCase().replace(/\s+/g, '');
      const bkgRange = String(booking.timeSlot || '').toLowerCase().replace(/\s+/g, '');
      return slotRange && bkgRange && slotRange === bkgRange;
    });

    if (matchedSlot) {
      const cfgPrice = resolveSlotPrice({
        serviceConfig: matchedConfig,
        slotId: matchedSlot.id,
        date: booking.date,
      });
      if (cfgPrice !== null && cfgPrice > 0) return cfgPrice;
    }
  }

  // All structured amounts were 0 or missing — fall back to booking.price
  return toAmount(booking.price) ?? 0;
};
